import React, { useState, useEffect, useRef } from 'react';
import { X, Check, CreditCard, Loader2, AlertCircle, CheckCircle, Plus, Zap, AlertTriangle } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useToken } from '../contexts/TokenContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

let stripePromiseCache: { [accountId: string]: Promise<any> } = {};

const getStripe = async (accountId?: string, forceReload: boolean = false) => {
  const cacheKey = accountId || 'default';
  
  // Reset cache if force reload
  if (forceReload) {
    delete stripePromiseCache[cacheKey];
  }
  
  if (!stripePromiseCache[cacheKey]) {
    try {
      const token = localStorage.getItem('token');
      const url = new URL(`${apiUrl}/api/stripe/config`);
      if (accountId) {
        url.searchParams.append('accountId', accountId);
      }
      
      const response = await fetch(url.toString(), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch Stripe config: ${response.status}`);
      }

      const data = await response.json();
      if (!data.success || !data.data?.publishableKey) {
        throw new Error('Stripe publishable key is missing');
      }

      console.log(`🔑 [STRIPE] Loading Stripe with ${data.data.mode} mode key for account ${accountId || 'default'}`);
      
      // Store mode for display (will be set in PaymentForm component)
      stripePromiseCache[cacheKey] = loadStripe(data.data.publishableKey);
    } catch (error) {
      console.error('Failed to load Stripe config:', error);
      throw error;
    }
  }
  return stripePromiseCache[cacheKey];
};

interface TokenPack {
  _id: string;
  name: string;
  description: string;
  tokenAmount: number;
  priceInCents: number;
  isPopular?: boolean;
}

interface PaymentMethod {
  id: string;
  type: 'card';
  brand: string;
  last4: string;
  expiryMonth: number;
  expiryYear: number;
  isDefault: boolean;
}

const PaymentForm: React.FC<{
  pack: TokenPack;
  onSuccess: () => void;
  onError: (error: string) => void;
}> = ({ pack, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const cardElementRef = useRef<any>(null);
  const [loading, setLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | null>(null);
  const [useNewCard, setUseNewCard] = useState(false);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(true);
  const [enableAutoReload, setEnableAutoReload] = useState(false);
  const [thresholdTokens, setThresholdTokens] = useState(10000);
  const [stripeMode, setStripeMode] = useState<'test' | 'live' | null>(null);

  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        setLoadingPaymentMethods(true);
        const token = localStorage.getItem('token');
        const response = await fetch(`${apiUrl}/api/stripe/payment-methods`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data && data.data.length > 0) {
            setPaymentMethods(data.data);
            const defaultMethod = data.data.find((pm: PaymentMethod) => pm.isDefault);
            if (defaultMethod) {
              setSelectedPaymentMethodId(defaultMethod.id);
              setUseNewCard(false);
            } else {
              setUseNewCard(true);
            }
          } else {
            setUseNewCard(true);
          }
        } else {
          setUseNewCard(true);
        }
      } catch (err) {
        console.error('Failed to fetch payment methods:', err);
        setUseNewCard(true);
      } finally {
        setLoadingPaymentMethods(false);
      }
    };

    const fetchStripeMode = async () => {
      try {
        const token = localStorage.getItem('token');
        
        // Decode token to get accountId
        const payload = JSON.parse(atob(token!.split('.')[1]));
        const accountId = payload.currentAccountId;
        
        const url = new URL(`${apiUrl}/api/stripe/config`);
        if (accountId) {
          url.searchParams.append('accountId', accountId);
        }
        
        const response = await fetch(url.toString(), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data?.mode) {
            console.log(`🔑 [STRIPE MODE UI] Fetched mode: ${data.data.mode} for account ${accountId}`);
            setStripeMode(data.data.mode);
          }
        }
      } catch (err) {
        console.error('Failed to fetch Stripe mode:', err);
      }
    };

    fetchPaymentMethods();
    fetchStripeMode();
  }, []);

  useEffect(() => {
    const cardElement = elements?.getElement(CardElement);
    if (cardElement) {
      cardElement.update({ disabled: !stripe || loading || !useNewCard });
    }
  }, [stripe, loading, elements, useNewCard]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!useNewCard && !selectedPaymentMethodId) {
      onError('Please select a payment method.');
      return;
    }

    if (useNewCard && (!stripe || !elements)) {
      onError('Payment system not ready. Please try again.');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required. Please log in again.');
      }

      let paymentMethodId: string | null = null;

      if (!useNewCard && selectedPaymentMethodId) {
        paymentMethodId = selectedPaymentMethodId;
      } else {
        if (!stripe || !elements) {
          throw new Error('Payment system not ready. Please try again.');
        }

        const setupController = new AbortController();
        const setupTimeout = setTimeout(() => setupController.abort(), 30000);

        let setupResponse;
        try {
          setupResponse = await fetch(`${apiUrl}/api/stripe/setup-intent`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            signal: setupController.signal,
          });
        } catch (fetchError: any) {
          clearTimeout(setupTimeout);
          if (fetchError.name === 'AbortError') {
            throw new Error('Request timed out. Please check your connection and try again.');
          }
          throw fetchError;
        }
        clearTimeout(setupTimeout);

        if (!setupResponse.ok) {
          const errorData = await setupResponse.json().catch(() => ({ message: 'Failed to create setup intent' }));
          throw new Error(errorData.message || `Server error: ${setupResponse.status}`);
        }

        const setupData = await setupResponse.json();
        if (!setupData.success) {
          throw new Error(setupData.message || 'Failed to create setup intent');
        }

        const cardElement = elements.getElement(CardElement);
        if (!cardElement) {
          throw new Error('Card element not found');
        }

        const { setupIntent, error: stripeError } = await stripe.confirmCardSetup(setupData.data.clientSecret, {
          payment_method: {
            card: cardElement,
          },
        });

        if (stripeError) {
          throw new Error(stripeError.message);
        }

        if (setupIntent?.status !== 'succeeded') {
          throw new Error('Card setup failed');
        }

        paymentMethodId = setupIntent.payment_method as string;
      }

      // Create payment intent for token purchase
      const purchaseController = new AbortController();
      const purchaseTimeout = setTimeout(() => purchaseController.abort(), 60000);

      let purchaseResponse;
      try {
        purchaseResponse = await fetch(`${apiUrl}/api/tokens/purchase`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            packId: pack._id,
            paymentMethodId,
            enableAutoReload: enableAutoReload && paymentMethodId ? true : false,
            thresholdTokens: enableAutoReload ? thresholdTokens : undefined,
          }),
          signal: purchaseController.signal,
        });
      } catch (fetchError: any) {
        clearTimeout(purchaseTimeout);
        if (fetchError.name === 'AbortError') {
          throw new Error('Purchase request timed out. Please check your connection and try again.');
        }
        throw fetchError;
      }
      clearTimeout(purchaseTimeout);

      if (!purchaseResponse.ok) {
        const errorData = await purchaseResponse.json().catch(() => ({ message: 'Failed to purchase tokens' }));
        throw new Error(errorData.message || `Server error: ${purchaseResponse.status}`);
      }

      const purchaseData = await purchaseResponse.json();
      if (!purchaseData.success) {
        throw new Error(purchaseData.message || 'Failed to purchase tokens');
      }

      // Confirm payment with Stripe
      if (!stripe) {
        throw new Error('Stripe not initialized');
      }

      const { paymentIntent, error: confirmError } = await stripe.confirmCardPayment(
        purchaseData.data.clientSecret,
        {
          payment_method: paymentMethodId!,
        }
      );

      if (confirmError) {
        throw new Error(confirmError.message);
      }

      if (paymentIntent?.status !== 'succeeded') {
        throw new Error('Payment failed');
      }

      // Confirm payment on backend to credit tokens (fallback for when webhooks aren't received)
      const confirmController = new AbortController();
      const confirmTimeout = setTimeout(() => confirmController.abort(), 30000);

      try {
        const confirmResponse = await fetch(`${apiUrl}/api/tokens/confirm-payment`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            paymentIntentId: paymentIntent.id,
          }),
          signal: confirmController.signal,
        });

        clearTimeout(confirmTimeout);

        if (!confirmResponse.ok) {
          console.error('Failed to confirm payment on backend, but payment succeeded');
        }
      } catch (confirmBackendError) {
        clearTimeout(confirmTimeout);
        console.error('Failed to confirm payment on backend:', confirmBackendError);
      }

      onSuccess();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to process payment');
    } finally {
      setLoading(false);
    }
  };

  const priceInDollars = pack.priceInCents / 100;
  const tokensFormatted = pack.tokenAmount >= 1000000 
    ? `${(pack.tokenAmount / 1000000).toFixed(1)}M` 
    : pack.tokenAmount >= 1000 
    ? `${(pack.tokenAmount / 1000).toFixed(0)}K` 
    : pack.tokenAmount.toString();

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Stripe Mode Indicator */}
      {stripeMode === 'test' && (
        <div className="p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
          <div className="flex items-start space-x-2">
            <AlertTriangle className="w-5 h-5 text-orange-600 dark:text-orange-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-orange-800 dark:text-orange-300">
                Test Mode Active
              </p>
              <p className="text-xs text-orange-700 dark:text-orange-400 mt-1">
                You're in test mode. Use test card <code className="bg-orange-100 dark:bg-orange-900/40 px-1 rounded">4242 4242 4242 4242</code> with any future expiry date and CVC.
              </p>
            </div>
          </div>
        </div>
      )}
      {stripeMode === 'live' && (
        <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
            <p className="text-sm font-medium text-green-800 dark:text-green-300">
              Live Mode - Real payments will be processed
            </p>
          </div>
        </div>
      )}

      {/* Pack Summary */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">{pack.name}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">{pack.description}</p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">${priceInDollars.toFixed(2)}</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">{tokensFormatted} tokens</div>
          </div>
        </div>
      </div>

      {/* Payment Method Selection */}
      {loadingPaymentMethods ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
          <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Loading payment methods...</span>
        </div>
      ) : paymentMethods.length > 0 ? (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Payment Method
          </label>

          <div className="space-y-2 mb-4">
            {paymentMethods.map((method) => (
              <div
                key={method.id}
                onClick={() => {
                  setSelectedPaymentMethodId(method.id);
                  setUseNewCard(false);
                }}
                className={`p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                  !useNewCard && selectedPaymentMethodId === method.id
                    ? 'border-primary-600 bg-primary-50 dark:bg-gray-700 dark:border-primary-800/50'
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-gray-100 capitalize">
                          {method.brand} •••• {method.last4}
                        </span>
                        {method.isDefault && (
                          <span className="text-xs px-2 py-0.5 bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300 rounded">
                            Default
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Expires {method.expiryMonth.toString().padStart(2, '0')}/{method.expiryYear}
                      </p>
                    </div>
                  </div>
                  {!useNewCard && selectedPaymentMethodId === method.id && (
                    <div className="w-5 h-5 rounded-full bg-primary-600 flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div
            onClick={() => {
              setUseNewCard(true);
              setSelectedPaymentMethodId(null);
            }}
            className={`p-3 border-2 rounded-lg cursor-pointer transition-colors mb-4 ${
              useNewCard
                ? 'border-primary-600 bg-primary-50 dark:bg-gray-700 dark:border-primary-800/50'
                : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Plus className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                <span className="font-medium text-gray-900 dark:text-gray-100">Use a new card</span>
              </div>
              {useNewCard && (
                <div className="w-5 h-5 rounded-full bg-primary-600 flex items-center justify-center">
                  <Check className="w-3 h-3 text-white" />
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {/* Card Information */}
      {useNewCard && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Card Information
          </label>
          <div className={`p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 ${!stripe || loading ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}>
            <CardElement
              onReady={(element) => {
                cardElementRef.current = element;
                element.update({ disabled: !stripe || loading });
              }}
              options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#424770',
                    '::placeholder': {
                      color: '#aab7c4',
                    },
                  },
                  invalid: {
                    color: '#9e2146',
                  },
                },
              }}
            />
          </div>
        </div>
      )}

      {/* Auto-reload Toggle */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={enableAutoReload}
            onChange={(e) => setEnableAutoReload(e.target.checked)}
            className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
          />
          <div className="flex-1">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Enable auto-reload</span>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Automatically purchase this pack when balance drops below threshold
            </p>
          </div>
        </label>
        {enableAutoReload && (
          <div className="mt-3 ml-7">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Reload when balance drops below:
            </label>
            <input
              type="number"
              min="0"
              value={thresholdTokens}
              onChange={(e) => setThresholdTokens(Math.max(0, parseInt(e.target.value) || 0))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {thresholdTokens.toLocaleString()} tokens
            </p>
          </div>
        )}
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={loading || !stripe}
        className="w-full bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Processing...
          </>
        ) : (
          <>
            <CreditCard className="w-5 h-5" />
            Purchase ${priceInDollars.toFixed(2)}
          </>
        )}
      </button>
    </form>
  );
};

const TokenPurchaseModal: React.FC = () => {
  const { isPurchaseModalOpen, closePurchaseModal, balance, freeTokensRemaining, refreshBalance } = useToken();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [step, setStep] = useState<'out-of-tokens' | 'payment' | 'success'>('out-of-tokens');
  const [selectedPack, setSelectedPack] = useState<TokenPack | null>(null);
  const [packs, setPacks] = useState<TokenPack[]>([]);
  const [loadingPacks, setLoadingPacks] = useState(true);
  const [stripeLoaded, setStripeLoaded] = useState(false);
  const [stripeInstance, setStripeInstance] = useState<any>(null);

  const isAccountOwner = user?.accountRole === 'Account Owner';

  useEffect(() => {
    if (isPurchaseModalOpen) {
      setStep('out-of-tokens');
      fetchPacks();
      loadStripeInstance();
    }
  }, [isPurchaseModalOpen]);

  const fetchPacks = async () => {
    try {
      setLoadingPacks(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/tokens/packs`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setPacks(data.data);
        }
      }
    } catch (err) {
      console.error('Failed to fetch token packs:', err);
    } finally {
      setLoadingPacks(false);
    }
  };

  const loadStripeInstance = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        // Decode token to get accountId
        const payload = JSON.parse(atob(token.split('.')[1]));
        const accountId = payload.currentAccountId;
        
        // Force reload Stripe instance to ensure we get the correct mode
        const stripe = await getStripe(accountId, true);
        setStripeInstance(stripe);
        setStripeLoaded(true);
      }
    } catch (err) {
      console.error('Failed to load Stripe:', err);
    }
  };

  const handlePackSelect = (pack: TokenPack) => {
    if (!isAccountOwner) {
      showToast('Only account owners can purchase tokens', 'error');
      return;
    }
    setSelectedPack(pack);
    setStep('payment');
  };

  const handlePaymentSuccess = async () => {
    setStep('success');
    await refreshBalance();
    setTimeout(() => {
      closePurchaseModal();
      setStep('out-of-tokens');
      setSelectedPack(null);
    }, 2000);
  };

  const handlePaymentError = (error: string) => {
    showToast(error, 'error');
  };

  if (!isPurchaseModalOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {step === 'out-of-tokens' && 'Out of Tokens'}
            {step === 'payment' && 'Purchase Tokens'}
            {step === 'success' && 'Purchase Successful'}
          </h2>
          {step !== 'success' && (
            <button
              onClick={closePurchaseModal}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="w-6 h-6" />
            </button>
          )}
        </div>

        <div className="p-6">
          {step === 'out-of-tokens' && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="mx-auto w-16 h-16 bg-warning-100 dark:bg-warning-900/30 rounded-full flex items-center justify-center mb-4">
                  <Zap className="w-8 h-8 text-warning-600 dark:text-warning-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  You've used all your tokens
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Current balance: {balance.toLocaleString()} tokens
                  {freeTokensRemaining > 0 && ` (${freeTokensRemaining.toLocaleString()} free remaining)`}
                </p>
              </div>

              {!isAccountOwner ? (
                <div className="bg-info-50 dark:bg-info-900/20 border border-info-200 dark:border-info-800 rounded-lg p-4">
                  <p className="text-sm text-info-800 dark:text-info-200">
                    Contact your account owner to purchase more tokens.
                  </p>
                </div>
              ) : (
                <>
                  {loadingPacks ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
                      <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Loading packs...</span>
                    </div>
                  ) : packs.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-sm text-gray-600 dark:text-gray-400">No token packs available</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {packs.map((pack) => {
                        const priceInDollars = pack.priceInCents / 100;
                        const tokensFormatted = pack.tokenAmount >= 1000000 
                          ? `${(pack.tokenAmount / 1000000).toFixed(1)}M` 
                          : pack.tokenAmount >= 1000 
                          ? `${(pack.tokenAmount / 1000).toFixed(0)}K` 
                          : pack.tokenAmount.toString();

                        return (
                          <button
                            key={pack._id}
                            onClick={() => handlePackSelect(pack)}
                            className={`w-full p-4 border-2 rounded-lg text-left transition-colors ${
                              pack.isPopular
                                ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20'
                                : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="flex items-center gap-2">
                                  <h4 className="font-semibold text-gray-900 dark:text-gray-100">{pack.name}</h4>
                                  {pack.isPopular && (
                                    <span className="text-xs px-2 py-0.5 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded">
                                      Popular
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{pack.description}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{tokensFormatted} tokens</p>
                              </div>
                              <div className="text-right">
                                <div className="text-xl font-bold text-gray-900 dark:text-gray-100">${priceInDollars.toFixed(2)}</div>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {step === 'payment' && selectedPack && (
            <div>
              {stripeLoaded && stripeInstance ? (
                <Elements stripe={stripeInstance}>
                  <PaymentForm pack={selectedPack} onSuccess={handlePaymentSuccess} onError={handlePaymentError} />
                </Elements>
              ) : (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
                  <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Loading payment form...</span>
                </div>
              )}
            </div>
          )}

          {step === 'success' && (
            <div className="text-center py-8">
              <div className="mx-auto w-16 h-16 bg-success-100 dark:bg-success-900/30 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-success-600 dark:text-success-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Tokens Purchased Successfully!
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {selectedPack && `${selectedPack.tokenAmount.toLocaleString()} tokens have been added to your account.`}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TokenPurchaseModal;
